import React from 'react'
import { Icon, Spinner, type IconSource } from '@/components/shopify-web-components'
import { ImageIcon } from '@shopify/polaris-icons'

type CompactImageUploadProps = {
  label: string
  displayImageUrl?: string
  hasImage: boolean
  disabled: boolean
  isUploading: boolean
  compactSize: number | 'fill'
  compactEmptyContent?: React.ReactNode
  compactOverlayLabel?: string | null
  hiddenInput: React.ReactNode
  onButtonClick: () => void
}

export function CompactImageUpload({
  label,
  displayImageUrl,
  hasImage,
  disabled,
  isUploading,
  compactSize,
  compactEmptyContent,
  compactOverlayLabel,
  hiddenInput,
  onButtonClick,
}: CompactImageUploadProps) {
  return (
    <>
      <button
        type="button"
        aria-label={hasImage ? `Replace ${label}` : `Upload ${label}`}
        onClick={onButtonClick}
        disabled={disabled || isUploading}
        className="giftie-compact-image-upload"
        style={{
          background: 'var(--p-color-bg-surface)',
          border: '1px solid var(--p-color-border)',
          borderRadius: 8,
          cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
          display: 'block',
          height: compactSize === 'fill' ? '100%' : compactSize,
          overflow: 'hidden',
          padding: 0,
          position: 'relative',
          width: compactSize === 'fill' ? '100%' : compactSize,
        }}
      >
        {hasImage
          ? (
              <img
                src={displayImageUrl}
                alt={label}
                style={{
                  display: 'block',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: isUploading ? 0.55 : 1,
                  width: '100%',
                }}
              />
            )
          : (
              <span
                style={{
                  alignItems: 'center',
                  color: 'var(--p-color-icon-secondary)',
                  display: 'flex',
                  height: '100%',
                  justifyContent: 'center',
                }}
              >
                {compactEmptyContent || <Icon source={ImageIcon as IconSource} />}
              </span>
            )}
        {(isUploading || compactOverlayLabel) && (
          <span
            className="giftie-compact-image-upload__overlay"
            style={{
              alignItems: 'center',
              background: isUploading
                ? 'rgba(15, 23, 42, 0.62)'
                : 'rgba(15, 23, 42, 0.74)',
              color: '#ffffff',
              display: 'flex',
              fontSize: 12,
              fontWeight: 600,
              inset: 0,
              justifyContent: 'center',
              lineHeight: 1.2,
              opacity: isUploading ? 1 : 0,
              padding: 10,
              position: 'absolute',
              textAlign: 'center',
              transition: 'opacity 120ms ease',
            }}
          >
            {isUploading ? <Spinner size="small" /> : compactOverlayLabel}
          </span>
        )}
      </button>
      <style jsx>
        {`
          .giftie-compact-image-upload:hover
            .giftie-compact-image-upload__overlay,
          .giftie-compact-image-upload:focus-visible
            .giftie-compact-image-upload__overlay {
            opacity: 1 !important;
          }
        `}
      </style>
      {hiddenInput}
    </>
  )
}
